/**
 * TS wrapper around the native iOS YouTube player plugin (CsYoutubePlayerPlugin).
 *
 * On the iOS native app, YouTube's inline iframe player fails with "Error 153"
 * because the page lives at capacitor://localhost and sends no usable referrer.
 * Instead of embedding the iframe, cs-markdown shows a thumbnail whose tap calls
 * this module, which opens a full-screen native player built on Google's
 * youtube-ios-player-helper. See design-docs/native-youtube-player.md for the
 * full background and the play()/reject/fallback contract.
 */
import { registerPlugin } from '@capacitor/core';
import { LoggingService } from 'services/logging.service';
import { isValidYoutubeId } from 'components/cs-markdown/video-embed';
import { openExternalLink } from 'util/open-external-link';

// The native plugin presents a full-screen YTPlayerView modal. Its one
// JS-visible method resolves once the player is on screen, and rejects with a
// coded error if the player itself fails (deleted / region-blocked video, etc.).
interface CsYoutubePlayerPluginInterface {
  play(options: { videoId: string }): Promise<void>;
}

const CsYoutubePlayerPlugin = registerPlugin<CsYoutubePlayerPluginInterface>('CsYoutubePlayerPlugin');

/**
 * Open a YouTube video in the full-screen native iOS player.
 *
 * This is only ever called from the thumbnail tap that buildYoutubeEmbedHtml
 * emits on the iOS native app. cs-markdown's openNativeYoutubePlayerAsync
 * delegates here.
 *
 * If the player reports an error — or the plugin call itself throws (should
 * never happen on an iOS build, where the plugin is always registered) — we fall
 * back to opening the YouTube watch page in the in-app Safari sheet, so the user
 * still gets to see the video (or YouTube's own explanation of why it cannot
 * play), and we report the failure.
 */
export async function playYoutubeNativeAsync(videoId: string, logging: LoggingService): Promise<void> {
  // refuse a malformed id. extractYoutubeId already validated the id upstream
  // before the thumbnail was ever emitted, so a false here means something went
  // wrong — surface it and stop. we do NOT build a watch URL or call the plugin
  // with an unvalidated id.
  if (!isValidYoutubeId(videoId)) {
    logging.trackSilentProblem('native-youtube-player-invalid-id', { videoId });
    return;
  }

  try {
    await CsYoutubePlayerPlugin.play({ videoId });
  } catch (err) {
    // fallback path (documented): the player or the plugin call failed. open the
    // watch page in the in-app Safari sheet so the user still sees the video or
    // YouTube's own error, and report the failure. `message` is the dependable
    // field; `code` is best-effort — a Capacitor plugin reject usually surfaces
    // as an Error-like object whose .message is the rejection string with no
    // .code attached, so code is reliably populated only when the native side
    // sets one.
    const code = (err as { code?: string })?.code;
    const message = (err as { message?: string })?.message;
    logging.trackSilentProblem('native-youtube-player-error', { videoId, code, message });
    await openExternalLink(`https://www.youtube.com/watch?v=${videoId}`);
  }
}
